import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowRight, Clock, User, Loader2, FileText, MessageSquare,
  ChevronDown, ChevronRight, Pill, FlaskConical
} from 'lucide-react';

function _formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function DelegatedBucketView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/my-day/delegated').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;

  if (!data || data.total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="p-4 rounded-2xl bg-muted/50 mb-4"><ArrowRight className="w-8 h-8 text-muted-foreground" /></div>
        <p className="text-sm font-medium mb-1">Nothing delegated</p>
        <p className="text-xs text-muted-foreground">Items you task to others will appear here until completed</p>
      </div>
    );
  }

  const SECTION_ICONS = {
    documents: FileText, messages: MessageSquare, prescriptions: Pill, labs: FlaskConical,
  };

  return (
    <div className="space-y-4 mt-1" data-testid="delegated-bucket">
      {Object.entries(data.sections).map(([key, section]) => {
        const SectionIcon = SECTION_ICONS[key] || ArrowRight;
        const items = section.items || [];
        const groups = section.groups || {};
        const hasGroups = Object.keys(groups).length > 0;

        return (
          <div key={key}>
            <div className="flex items-center gap-2 mb-2">
              <SectionIcon className="w-3.5 h-3.5 text-blue-500" />
              <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">
                {section.label} ({section.count})
              </p>
            </div>

            {hasGroups ? (
              Object.entries(groups).map(([groupName, groupItems]) => (
                <div key={groupName} className="mb-2">
                  <p className="text-[9px] text-slate-400 font-medium mb-1 pl-5">{groupName} ({groupItems.length})</p>
                  <div className="space-y-1">
                    {groupItems.map(item => (
                      <DelegatedItemCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="space-y-1">
                {items.map(item => (
                  <DelegatedItemCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function DelegatedItemCard({ item }) {
  const timeAgo = item.signed_at || item.last_message_at;
  return (
    <div className="rounded-lg border-l-4 border-l-blue-300 border bg-white px-4 py-2.5 flex items-center gap-3"
      data-testid={`delegated-item-${item.id}`}>
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-slate-700 truncate block">{item.title}</span>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-slate-400">{item.patient_name}</span>
          {item.tasked_to && <span className="text-[9px] text-blue-500 truncate">→ {item.tasked_to}</span>}
          {item.last_message && <span className="text-[9px] text-slate-400 truncate">{item.last_message}</span>}
        </div>
      </div>
      <Badge variant="outline" className="text-[8px] bg-blue-50 text-blue-500 border-blue-200 shrink-0">Pending</Badge>
    </div>
  );
}




export { DelegatedBucketView, DelegatedItemCard };
